import { Capacitor } from '@capacitor/core';
import { hasSignedInBefore } from '../../session/signedInBefore.js';

const KEY = 'peopleorbit_intro_seen';

/**
 * Whether the phone app's intro slides have been shown on this install. Leaving the intro by any
 * button marks it seen, so the next launch goes straight to sign-up (which keeps its own resumable
 * draft) instead of replaying the slides. Storage that cannot be read counts as seen: the intro is a
 * nicety, and a device that cannot remember it must never be trapped in it on every launch.
 */
export function hasSeenIntro(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return true;
  }
}

export function markIntroSeen(): void {
  try {
    localStorage.setItem(KEY, '1');
  } catch {
    // Storage unavailable: hasSeenIntro() already reads as seen, so nobody loops back here.
  }
}

/**
 * Whether this visitor may see /intro at all: the phone app, for someone who has never signed in on
 * this device. The website keeps its own landing, and a returning employee whose session lapsed
 * belongs on sign-in, not on a sales pitch. IntroPage redirects to /signup when this is false, so a
 * stray link to /intro cannot show it to the wrong person. Deliberately ignores hasSeenIntro():
 * RequireAuth decides whether to *send* someone here; this only decides whether they may stay.
 */
export function introEligible(): boolean {
  return Capacitor.isNativePlatform() && !hasSignedInBefore();
}
